import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import classes from './ProductsField.module.scss';

// The legacy prepaid details are no longer edited on the MO screen. Keep
// stable defaults for newly selected products while preserving any existing
// saved prepaidConfig verbatim.
const DEFAULT_PREPAID_CONFIG = {
  totalHours: 1,
  hoursInIncrement: 1,
  currencyIsoCode: 'USD',
  costPerHour: 1,
  discountPercents: 0,
};

const generateDefaultPurchase = (productId) => ({
  productId,
  plans: 0,
  addons: 0,
  prepaidConfig: { ...DEFAULT_PREPAID_CONFIG },
});

// Always apply the same A-Z ordering regardless of the user's browser locale.
const productNameCollator = new Intl.Collator('en', {
  sensitivity: 'base',
  numeric: true,
});

const ProductsField = ({
  products = [],
  value = [],
  onChange,
  usingPlans = {},
  disabled,
  assignedProducts = {},
}) => {
  const [selectOpen, setSelectOpen] = useState(false);
  const [search, setSearch] = useState('');

  // redux-form is the only source of truth for selected products. Keeping a
  // second selectedProductIds state allowed a stale prop/effect to undo a click.
  const selectedProductIds = useMemo(
    () => value.map((item) => String(item.productId)),
    [value]
  );

  // Sort on every render, including when the common products array is updated
  // in place. Do not mutate the Redux/common array or reorder saved purchases.
  const sortedProducts = [...products].sort((a, b) =>
    productNameCollator.compare(
      String(a.name || '').trim(),
      String(b.name || '').trim()
    ) || String(a._id || '').localeCompare(String(b._id || ''))
  );

  // One alphabetical run per visual column: A..middle on the left, then
  // middle..Z on the right. On narrow screens CSS stacks those two columns.
  const splitIndex = Math.ceil(sortedProducts.length / 2);
  const productColumns = [
    sortedProducts.slice(0, splitIndex),
    sortedProducts.slice(splitIndex),
  ];

  const selectedProducts = useMemo(() => {
    const selected = new Set(selectedProductIds);
    return sortedProducts.filter((product) => selected.has(String(product._id)));
  }, [sortedProducts, selectedProductIds]);

  const visibleProducts = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return selectedProducts;

    return selectedProducts.filter((product) =>
      String(product.name || '')
        .toLowerCase()
        .includes(normalized)
    );
  }, [selectedProducts, search]);

  const changePurchase = (productId, updates) => {
    const purchasedProduct =
      _.find(value, {
        productId,
      }) || generateDefaultPurchase(productId);

    const changedPurchasedProduct = {
      ...purchasedProduct,
      ...updates,
    };

    const updatedValue = _.reject(value, { productId });

    // Product membership is controlled by the Select block. Keep a selected
    // product even when it currently has 0 plans so the MO association does
    // not disappear while the admin edits it.
    if (selectedProductIds.map(String).includes(String(productId))) {
      updatedValue.push(changedPurchasedProduct);
    }

    onChange(updatedValue);
  };

  const canRemoveProduct = (productId) => {
    const assigned = assignedProducts[productId] || {};
    const using = usingPlans[productId] || {};
    const hasAssignedProduct = Object.prototype.hasOwnProperty.call(
      assignedProducts,
      productId
    );

    return !(
      hasAssignedProduct ||
      assigned.unlimited ||
      using.unlimited ||
      (assigned.plans || 0) + (using.plans || 0)
    );
  };

  const toggleSelectedProduct = (product) => {
    const productId = String(product._id);
    const selected = selectedProductIds.includes(productId);

    if (selected && !canRemoveProduct(product._id)) return;

    if (selected) {
      const nextValue = value.filter(
        (item) => String(item.productId) !== productId
      );
      onChange(nextValue);
      return;
    }

    const existing = value.find(
      (item) => String(item.productId) === productId
    );
    const nextValue = [
      ...value,
      existing || generateDefaultPurchase(product._id),
    ];

    onChange(nextValue);
  };

  const resetSearch = () => setSearch('');

  return (
    <div>
      <div className={classes.controls}>
        <label htmlFor="mo-product-search">Search</label>
        <input
          id="mo-product-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          autoComplete="off"
          placeholder="Product name"
        />
        {search && (
          <Button size="small" onClick={resetSearch}>
            Reset Search
          </Button>
        )}
        {!disabled && (
          <Button
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => setSelectOpen(true)}
          >
            Select
          </Button>
        )}
      </div>

      <div className={classes.productsArea}>
        <div className={classes.productList}>
          {visibleProducts.length === 0 ? (
            <div className={classes.empty} role="status" aria-live="polite">
              No Products
            </div>
          ) : (
            visibleProducts.map((product) => {
              let purchasedProduct = _.find(value, {
                productId: product._id,
              });
              purchasedProduct =
                purchasedProduct || generateDefaultPurchase(product._id);

              const assignedProduct = assignedProducts[product._id] || {};
              const usingProduct = usingPlans[product._id] || {};

              const plansPurchased = Number(purchasedProduct.plans || 0);
              const plansAvailable = purchasedProduct.unlimited
                ? 'Unlimited'
                : plansPurchased - (usingProduct.plans || 0);
              const plansAssigned = assignedProduct.plans || 0;
              const plansAssignable = purchasedProduct.unlimited
                ? 'Unlimited'
                : plansPurchased - plansAssigned || 0;

              const changePurchasedPlans = (e) => {
                let newVal = +e.target.value;

                if (newVal < plansAssigned) {
                  alert(
                    'You cant purchase less than already assigned to regions and learning centers'
                  );
                  newVal = plansAssigned;
                }

                changePurchase(product._id, { plans: newVal });
              };

              return (
                <div className={classes.wrapper} key={product._id}>
                  <b className={classes.productName}>{product.name}</b>

                  <div className={classes.planColumn}>
                    <div className={classes.fieldGroup}>
                      <label>
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={purchasedProduct.unlimited || false}
                          onChange={(e) => {
                            changePurchase(product._id, {
                              unlimited: e.target.checked,
                            });
                          }}
                        />
                        Unlimited Plans
                      </label>
                    </div>

                    {!purchasedProduct.unlimited && (
                      <>
                        <div className={classes.fieldGroup}>
                          <label>Plans Purchased:</label>
                          <input
                            type="number"
                            min={plansAssigned}
                            disabled={disabled}
                            value={plansPurchased}
                            onChange={changePurchasedPlans}
                          />
                        </div>
                        <div className={classes.fieldGroup}>
                          <label>Plans assigned:</label>
                          <input disabled type="number" value={plansAssigned} />
                        </div>
                        <div className={classes.fieldGroup}>
                          <label>Plans Assignable:</label>
                          <input disabled type="text" value={plansAssignable} />
                        </div>
                        <div className={classes.fieldGroup}>
                          <label>Plans Available:</label>
                          <input disabled type="text" value={plansAvailable} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>

      <Dialog
        open={selectOpen && !disabled}
        onClose={() => setSelectOpen(false)}
        fullWidth
        maxWidth="lg"
        classes={{ paper: classes.selectDialogPaper }}
      >
        <DialogTitle>Select Product (test)</DialogTitle>

        <DialogContent dividers className={classes.selectDialogContent}>
          <div className={classes.selectionGrid}>
            {productColumns.map((column, columnIndex) => (
              <div
                key={columnIndex}
                style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
              >
                {column.map((product) => {
                  const productId = String(product._id);
                  const selected = selectedProductIds.includes(productId);
                  const cannotRemove = selected && !canRemoveProduct(product._id);

                  return (
                    <FormControlLabel
                      key={product._id}
                      className={classes.selectionItem}
                      control={
                        <Checkbox
                          color="primary"
                          checked={selected}
                          disabled={cannotRemove}
                          onChange={() => toggleSelectedProduct(product)}
                        />
                      }
                      label={product.name}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

ProductsField.propTypes = {
  products: PropTypes.array,
  value: PropTypes.array,
  available: PropTypes.array,
  plansType: PropTypes.string.isRequired,
  showOnlyPurchased: PropTypes.bool,
};

export default ProductsField;
